import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, BookOpen, Briefcase, Clock, Hourglass, ListChecks, Loader2, UserCheck } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Card, Pill, PrimaryButton } from './ui';
import { STATE_META, studentApi } from '../../pages/journey/journeyApi';

const Feedback = ({ sub, rubric }) => {
  if (sub.status === 'submitted') return (
    <div className="flex items-start gap-3 text-sm text-[#0b1f4d] bg-teal-50 rounded-xl p-4" data-testid={`submission-awaiting-${sub.attempt}`}>
      <Hourglass className="w-4 h-4 mt-0.5" /> Submitted. Your Elevate consultant will review it and share feedback here. Your next task opens after the review.
    </div>);
  const revise = sub.status === 'revision_requested';
  const names = Object.fromEntries(rubric.map((r) => [r.id, r.name]));
  return (
    <div data-testid={`submission-feedback-${sub.attempt}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Pill className={revise ? 'bg-amber-50 text-amber-700' : 'bg-emerald-100 text-emerald-800'} data-testid={`feedback-label-${sub.attempt}`}>
          <UserCheck className="w-3.5 h-3.5" /> {sub.feedback_label} · {revise ? 'please revise' : 'approved'}
        </Pill>
        <span className="font-outfit text-2xl font-bold text-[#0b1f4d]" data-testid={`submission-score-${sub.attempt}`}>{sub.score}%</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        {sub.criteria.map((c) => (
          <div key={c.id} className="rounded-xl bg-slate-50 p-3 text-sm">
            <div className="flex justify-between font-medium"><span>{names[c.id]}</span><span>{c.score}/4</span></div>
            <p className="text-xs text-slate-600 mt-1">{c.comment}</p>
          </div>))}
      </div>
      {sub.feedback.reviewer_comment && <p className="text-sm bg-emerald-50 rounded-xl p-3 mb-3"><strong>Consultant's note:</strong> {sub.feedback.reviewer_comment}</p>}
      {sub.feedback.strengths?.length > 0 && <p className="text-sm"><strong className="text-teal-700">Strengths:</strong> {sub.feedback.strengths.join(' · ')}</p>}
      {sub.feedback.improvements?.length > 0 && <ul className="text-sm mt-2 space-y-1"><strong className="text-amber-700">Improve:</strong>{sub.feedback.improvements.map((x) => <li key={x}>• {x}</li>)}</ul>}
    </div>
  );
};

const Linkify = ({ text }) => text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) => (/^https?:\/\//.test(part)
  ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline break-all">{part}</a> : part));

const List = ({ title, items, ordered, testid, Icon }) => (
  <div className="mt-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-teal-700 mb-2 flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{title}</p>
    {ordered
      ? <ol className="space-y-2">{items.map((x, i) => <li key={x} className="flex gap-3 text-sm text-slate-700"><span className="w-5 h-5 shrink-0 rounded-full bg-[#0b1f4d] text-teal-300 text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span><span><Linkify text={x} /></span></li>)}</ol>
      : <ul className="space-y-1.5">{items.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-700"><span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2 shrink-0" /><span><Linkify text={x} /></span></li>)}</ul>}
  </div>
);

const Brief = ({ m }) => (
  <Card>
    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line" data-testid="mission-brief">{m.brief}</p>
    <p className="text-sm mt-4"><strong>Deliverable:</strong> {m.deliverable}</p>
    {m.employer_check?.length > 0 && <List title="What employers check" items={m.employer_check} Icon={Briefcase} testid="mission-employer-check" />}
    {m.steps?.length > 0 && <List title="Do it yourself, step by step" items={m.steps} ordered Icon={ListChecks} testid="mission-steps" />}
    {m.resources?.length > 0 && <List title="Free resources" items={m.resources} Icon={BookOpen} testid="mission-resources" />}
    {m.portfolio_tip && <p className="text-sm mt-5 rounded-xl bg-teal-50 border border-teal-100 p-3" data-testid="mission-portfolio-tip"><strong className="text-teal-800">Show it off:</strong> {m.portfolio_tip}</p>}
    {m.stretch && <p className="text-sm mt-3 rounded-xl bg-[#0b1f4d] text-slate-200 p-3" data-testid="mission-stretch"><strong className="text-teal-300">Experienced? Do this instead:</strong> {m.stretch}</p>}
    <p className="text-xs uppercase tracking-widest text-teal-700 mt-6 mb-2">What your consultant looks for</p>
    <div className="grid sm:grid-cols-2 gap-2">{m.rubric.map((r) => <div key={r.id} className="text-xs rounded-lg border border-slate-200 p-2.5"><strong>{r.name}</strong>: {r.excellent}</div>)}</div>
  </Card>
);

export const MissionView = ({ missionId, studentId, refresh, onBack }) => {
  const [m, setM] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => studentApi(`/me/missions/${missionId}?student_id=${studentId}`).then(setM).catch((e) => { toast.error(e.message); onBack(); });
  useEffect(() => { load(); }, [missionId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return <div className="h-40 rounded-2xl bg-white animate-pulse" />;
  const mission = m.mission;
  const canSubmit = !['locked', 'awaiting_review', 'validated', 'completed', 'placed_out'].includes(m.state);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await studentApi(`/me/missions/${missionId}/submit`, { method: 'POST', body: { student_id: studentId, content: text } });
      toast.success(r.status === 'submitted' ? 'Submitted for consultant review' : `Feedback ready: ${r.score}%`);
      setText(''); await load(); refresh();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div data-testid="mission-view">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-5" data-testid="mission-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Pill className={STATE_META[m.state].cls} data-testid="mission-state">{STATE_META[m.state].label}</Pill>
        <Pill className="bg-slate-100 text-slate-600 capitalize">{mission.kind === mission.level ? mission.kind : `${mission.kind} · ${mission.level}`}</Pill>
        {mission.requires_specialist && <Pill className="bg-teal-50 text-teal-700">Specialist review</Pill>}
        {mission.time_estimate && <Pill className="bg-slate-100 text-slate-600" data-testid="mission-time"><Clock className="w-3 h-3" />{mission.time_estimate}</Pill>}
        {mission.track === 'professional' && <Pill className="bg-[#0b1f4d] text-teal-300">Professional track</Pill>}
      </div>
      <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0b1f4d] mb-6" data-testid="mission-title">{mission.title}</h1>
      <div className="grid xl:grid-cols-[1fr_1fr] gap-5">
        <Brief m={mission} />
        {canSubmit ? (
          <Card>
            <p className="font-outfit font-semibold text-[#0b1f4d] mb-3">{m.submissions.length ? `Attempt ${m.submissions.length + 1}` : 'Your submission'}</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={busy} rows={10} data-testid="mission-submission-input"
              placeholder="Paste your work here (code, text or links)…" className="font-mono text-sm" />
            <PrimaryButton onClick={submit} disabled={busy || text.trim().length < 20} className="mt-3 w-full" data-testid="mission-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for review'}
            </PrimaryButton>
            <p className="text-xs text-slate-500 mt-2">Every submission is reviewed by an Elevate consultant against the checklist{mission.requires_specialist ? ' (projects are reviewed by a specialist)' : ''}.</p>
          </Card>
        ) : (
          <Card className="flex items-center" data-testid="mission-closed-note">
            <p className="text-sm text-slate-600">{m.state === 'awaiting_review' ? 'Your work is with your consultant. Feedback will appear below, and your next task opens after the review.'
              : m.state === 'placed_out' ? 'Your consultant confirmed you can skip this one based on your starter check. It does not count toward your progress.' : 'Well done, this task is approved. Head back to your journey for the next one.'}</p>
          </Card>
        )}
      </div>
      <div className="mt-6 space-y-4" data-testid="submission-history">
        {[...m.submissions].reverse().map((s) => (
          <Card key={s.id}><p className="text-xs text-slate-500 mb-3">Attempt {s.attempt} · {new Date(s.created_at).toLocaleString('en-IN')}</p><Feedback sub={s} rubric={mission.rubric} /></Card>))}
      </div>
    </div>
  );
};
